import * as React from 'react';
import { ComponentProps, ReactElement, useMemo } from 'react';

import { useAtomValue } from 'jotai';

import { translate } from '~l10n/client';
import { languageAtom } from '~shared/atoms';
import { formatBytes } from '~teacher/components/FileDrop/utilities/formatBytes';

import { FileDropContainer } from './FileDropContainer';
import { FileHandlerConfig } from './FileHandlers/FileHandlerConfig';
import { useFiles } from './hooks/useProcessFiles';

export type FileDropUploadState = 'idle' | 'inprogress' | 'error';

interface FileDropProps extends FileHandlerConfig {
  /** The label for the file drop */
  label: React.ReactElement<ComponentProps<typeof FileDrop.Label>>;
  helperText?: ReactElement<ComponentProps<typeof FileDrop.HelperText>>;
  /** The maximum number of files that can be uploaded */
  maxFiles: number;
  /** The maximum file size in bytes */
  maxFileSize: number;
  /** Whether the file drop is disabled */
  isDisabled?: boolean;
  /**
   * Optional callback that is invoked whenever the upload state changes.
   * Reports whether there are any files currently being uploaded (pending or in progress).
   * This is useful for disabling form submission or showing loading states while uploads are in progress.
   *
   * @param uploadState - The current upload state
   */
  onUploadStateChange?: (uploadState: FileDropUploadState) => void;
  /**
   * Optional callback that is invoked when a file is removed.
   * This allows the parent component to clear any associated state (e.g., fileKey or fileText).
   */
  onFileRemove?: () => void;
}

export function FileDrop({
  isDisabled = false,
  maxFileSize,
  mimeTypeMap,
  allMimeTypes,
  commonExtensions,
  maxFiles,
  onUploadStateChange,
  onFileRemove,
  label,
  helperText,
}: FileDropProps) {
  const language = useAtomValue(languageAtom);
  const { tasks, dispatch } = useFiles({
    maxFiles,
    maxFileSize,
    mimeTypeMap,
    allMimeTypes,
  });

  const uploadState = React.useMemo(() => {
    if (
      tasks.some(
        (task) => task.status === 'pending' || task.status === 'progress'
      )
    ) {
      return 'inprogress';
    } else if (tasks.some((task) => task.status === 'error')) {
      return 'error';
    } else {
      return 'idle';
    }
  }, [tasks]);

  // Notify parent component when upload state changes
  React.useEffect(() => {
    onUploadStateChange?.(uploadState);
  }, [uploadState, onUploadStateChange]);

  const acceptedFileTypes = Array.from(commonExtensions).map(
    (ext) => `.${ext}`
  );

  const hasUploadedMaxFileCount = maxFiles === tasks.length;
  const hasSingleFileError = maxFiles === 1 && tasks[0]?.status === 'error';

  const disableControl = useMemo(() => {
    if (isDisabled) {
      return true;
    }

    // If this control only allows a single file, and it had an error
    // do not block uploads
    if (hasSingleFileError) {
      return false;
    }
    return hasUploadedMaxFileCount;
  }, [isDisabled, hasUploadedMaxFileCount, hasSingleFileError]);

  return (
    <FileDropContainer
      acceptedFileTypes={acceptedFileTypes}
      dispatch={dispatch}
      currentFilesCount={tasks.length}
      maxFiles={maxFiles}
      label={label}
      files={tasks}
      isDisabled={disableControl}
      onFileRemove={onFileRemove}
      fileDropButtonText={
        <FileDropContainer.ButtonText>
          {maxFiles > 1
            ? translate('Drag files here or click to upload')
            : translate('Drag a file here or click to upload')}
        </FileDropContainer.ButtonText>
      }
      fileDropButtonHelperText={
        <FileDropContainer.ButtonHelperText>
          {maxFiles > 1 ? (
            <>
              {translate('Max {0}').format(
                formatBytes({ bytes: maxFileSize, locale: language })
              )}{' '}
              • {acceptedFileTypes.join(' ')}
            </>
          ) : tasks.length === 0 || hasSingleFileError ? (
            <>
              {translate('Max {0}').format(
                formatBytes({ bytes: maxFileSize, locale: language })
              )}{' '}
              • {acceptedFileTypes.join(' ')}
            </>
          ) : (
            <>{translate('Remove the file to upload another.')}</>
          )}
        </FileDropContainer.ButtonHelperText>
      }
      helperText={helperText}
    />
  );
}

FileDrop.Label = FileDropContainer.Label;
FileDrop.HelperText = FileDropContainer.HelperText;
